import { toast } from 'sonner';
import type { TFunction } from 'i18next';
import type { DraftRowShape } from '@/lib/draft-rows/draftRow';
import { buildAnchorImportPayload } from './utils/buildAnchorImportPayload';
import { classifyAnchorDrafts } from './utils/classifyAnchorDrafts';
import { commitAnchorDelta } from './utils/commitAnchorDelta';
import {
  effectiveAnchorBaseline,
  isClearedAnchorDraft,
  type WizardFlushBaselines
} from './utils/flushBaselines';
import { validateDraftRows } from '@/lib/draft-rows/markDuplicateRows';
import {
  effectiveAnchorRows,
  isProtectedRow,
  NO_PROTECTED_IDS,
  partitionProtectedRows,
  requireProtectedCommit
} from './utils/protectedRows';
import type { AnchorStepSpec, WizardSeeds } from './anchorStepSpecs';
import type { WizardValidationContext } from './useWizardValidationContext';
import type { WizardDrafts } from './useWizardSubmit';
import type { WizardProgress } from './wizardSteps';

/**
 * Shared Weiter flow of the two structural-anchor steps: validate the draft,
 * short-circuit an untouched seeded table into pure navigation, else send the
 * classified delta (adds append, seeded edits update in place, seeded
 * removals delete surgically — ADR 0011, third amendment) and reset the draft
 * back to the refetched seed view. Protected rows (locked-identity members)
 * are partitioned out before the classify and commit via their own placement
 * endpoint. Consumes the same AnchorStepSpec as the silent flush and the
 * dirty check. Module-scoped like the optional steps' runStepMutation:
 * everything hook-owned arrives via config.
 */
export async function submitAnchorStep<
  TData extends object,
  TRow extends DraftRowShape<TData>,
  TRequest,
  TResponse
>(config: {
  spec: AnchorStepSpec<TData, TRow, TRequest, TResponse>;
  drafts: WizardDrafts;
  baselines: WizardFlushBaselines;
  seeds: WizardSeeds;
  progress: WizardProgress;
  validation: WizardValidationContext;
  setRows: React.Dispatch<React.SetStateAction<TRow[]>>;
  /** Drops the step's flush baseline — always paired with the reset below. */
  clearBaseline: () => void;
  mutate: (request: TRequest) => Promise<TResponse>;
  /** Commits changed protected rows via their placement endpoint (users step only). */
  protectedCommit?: {
    commitRows: (rows: Array<TRow & { entityId: string }>) => Promise<unknown>;
    failedToastKey: string;
  };
  navigateNext: () => void;
  /** Required on the two structural anchors; optional steps never error on an empty draft. */
  validationRequiredKey?: string;
  fixErrorsKey: string;
  createSuccessKey: string;
  createFailedKey: string;
  t: TFunction;
  onApiError: (error: unknown, failedToastKey: string) => void;
}): Promise<void> {
  const { spec, t, validation, progress } = config;
  const rawDraftRows = spec.selectDrafts(config.drafts);
  const seedRows = spec.selectSeeds(config.seeds);
  const protectedIds = spec.selectProtectedIds?.(validation, progress) ?? NO_PROTECTED_IDS;

  // Protected rows demand validation even when the draft never materialized
  // them — Weiter must not navigate past a protected member without a
  // department. effectiveAnchorRows is the SAME rule the table displays and
  // the Weiter-enable gate counts, so what the user sees is what submits.
  const draftRows = effectiveAnchorRows({
    draftRows: rawDraftRows,
    seedRows,
    protectedIds,
    isDone: spec.isDone(progress),
    baseline: spec.selectBaseline(config.baselines)
  });

  const baseline = effectiveAnchorBaseline(
    spec.selectBaseline(config.baselines),
    spec.isDone(progress),
    seedRows,
    draftRows
  );

  // Fall back to the seeded view, keeping any row that changed AFTER the sent
  // snapshot (a cell edited while the request was in flight must survive the
  // reset). Dropping the flush baseline travels with the reset — a stale
  // baseline would mis-classify the next visit.
  const resetToSeedDisplay = (sentRows: readonly TRow[]) => {
    const sent = new Set<TRow>(sentRows);
    config.setRows((current) => current.filter((row) => !sent.has(row)));
    config.clearBaseline();
  };

  // The classifier owns every empty-draft verdict: an untouched done step
  // and an empty optional step classify as unchanged (pure navigation,
  // identical to Überspringen), a cleared table ("Liste leeren" / every row
  // deleted, baseline surviving) classifies into a removals-only delta.
  // Only a fresh, required anchor with nothing entered cannot be expressed
  // as a delta — it errors.
  if (
    draftRows.length === 0 &&
    !spec.isDone(progress) &&
    !spec.optionalStep &&
    !isClearedAnchorDraft(draftRows, baseline, protectedIds)
  ) {
    if (config.validationRequiredKey) toast.error(t(config.validationRequiredKey));
    return;
  }
  const { rows, allValid } = validateDraftRows(
    draftRows,
    spec.buildValidateRow(validation),
    spec.duplicateChecks,
    spec.buildCrossRowChecks(validation, progress)
  );
  config.setRows(rows);
  if (!allValid) {
    toast.error(t(config.fixErrorsKey));
    return;
  }
  const { editableRows, editableBaseline, changedProtectedRows } = partitionProtectedRows(
    rows,
    baseline,
    seedRows,
    protectedIds
  );
  // Sequential, idempotent placement PATCHes (see commitPlacementRows) —
  // protected rows never travel through the add-users pipeline (the backend
  // rejects them wholesale). A not-yet-done step commits EVERY protected row,
  // changed or not: the placement PATCH is what stamps the step's
  // initialization timestamp, and a protected-rows-only table whose placements
  // already match the seeds would otherwise never complete the step (nothing
  // else stamps it — the exact wedge of the solo precreated admin).
  const protectedRowsToCommit = spec.isDone(progress)
    ? changedProtectedRows
    : rows.filter((row): row is TRow & { entityId: string } => isProtectedRow(row, protectedIds));
  if (protectedRowsToCommit.length > 0) {
    const protectedCommit = requireProtectedCommit(
      config.protectedCommit,
      protectedRowsToCommit.length
    );
    try {
      await protectedCommit.commitRows(protectedRowsToCommit);
    } catch (error) {
      config.onApiError(error, protectedCommit.failedToastKey);
      return;
    }
  }
  const classification = classifyAnchorDrafts(editableBaseline, editableRows);
  if (classification.kind === 'unchanged') {
    resetToSeedDisplay(rows);
    config.navigateNext();
    return;
  }
  try {
    await commitAnchorDelta({
      spec,
      validation,
      payload: buildAnchorImportPayload(classification),
      createRows: classification.newRows,
      allRows: rows,
      mutate: config.mutate
    });
    toast.success(t(config.createSuccessKey));
    // The awaited invalidations already refetched the import including the
    // new rows; keeping the sent drafts would false-flag removals on the
    // next visit.
    resetToSeedDisplay(rows);
    config.navigateNext();
  } catch (error) {
    config.onApiError(error, config.createFailedKey);
  }
}
